import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Heart, Bookmark, ShoppingBag, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { readCart, writeCart } from '../lib/shopCart';
import DynamicPage from './DynamicPage';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, getShopProduct, likeProduct, favouriteProduct, recordProductView } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [idx, setIdx] = useState(0);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    getShopProduct(productId).then((d) => { setP(d); recordProductView(d.id); document.title = `${d.name} · Shop`; }).catch(() => setErr('This product is no longer available.'));
    /* eslint-disable-next-line */
  }, [productId]);

  const toggleLike = async () => { if (!user) { toast.info('Sign in to like items.'); return; } const r = await likeProduct(p.id); setP({ ...p, liked: r.liked, like_count: r.like_count }); };
  const toggleFav = async () => { if (!user) { toast.info('Sign in to save favourites.'); return; } const r = await favouriteProduct(p.id); setP({ ...p, favourited: r.favourited, favourite_count: r.favourite_count }); toast.success(r.favourited ? 'Saved to your favourites.' : 'Removed from favourites.'); };
  const addToCart = () => {
    const cart = readCart();
    if (p.custom_amount) {
      const d = parseInt(amount, 10);
      if (!d || d * 100 < (p.min_amount || 500)) { toast.error(`Please enter a whole-dollar amount of at least $${(p.min_amount || 500) / 100}.`); return; }
      cart.items[p.id] = 1; cart.custom[p.id] = String(d);
    } else {
      cart.items[p.id] = Math.min((cart.items[p.id] || 0) + 1, p.track_stock === false ? 99 : p.stock);
    }
    writeCart(cart);
    toast.success(`${p.name} added to your cart.`, { action: { label: 'View cart', onClick: () => navigate('/shop') } });
  };

  if (err && pathname.startsWith('/p/')) return <DynamicPage />;
  if (err) return <div className="max-w-3xl mx-auto px-4 py-12" data-testid="product-missing"><p>{err}</p><Link to="/shop" className="underline" style={{ color: 'var(--navy)' }}>Back to the Shop</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-4 py-12 text-sm inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  const imgs = p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []);
  const inStock = p.track_stock === false || p.stock > 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 fade-in" data-testid="product-detail">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm mb-4 underline" style={{ color: 'var(--navy)' }} data-testid="product-back"><ArrowLeft size={14} /> Back to the Shop</Link>
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          {imgs.length ? (
            <>
              <div className="rounded-lg overflow-hidden border bg-white" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <img src={imgs[idx] || imgs[0]} alt={p.name} className="w-full h-96 object-contain" data-testid="product-main-image" />
              </div>
              {imgs.length > 1 && (
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="product-thumbs">
                  {imgs.map((u, i) => <button key={u + i} onClick={() => setIdx(i)} className="w-16 h-16 rounded overflow-hidden border-2" style={{ borderColor: i === idx ? 'var(--maroon)' : 'transparent' }} data-testid={`product-thumb-${i}`}><img src={u} alt="" className="w-full h-full object-cover" /></button>)}
                </div>
              )}
            </>
          ) : <div className="w-full h-64 rounded-lg flex items-center justify-center" style={{ background: 'var(--sage)' }}><ShoppingBag size={40} style={{ color: 'var(--olive)' }} /></div>}
        </div>
        <div>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)' }} data-testid="product-category">{p.category}</span>
          <h1 className="font-title text-3xl md:text-4xl mt-2 mb-2" style={{ color: 'var(--maroon)' }} data-testid="product-name">{p.name}</h1>
          {p.custom_amount ? (
            <label className="flex items-center gap-2 font-title text-2xl mb-3" style={{ color: 'var(--maroon)' }}>$
              <input type="number" min={(p.min_amount || 500) / 100} step="1" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} placeholder="25" data-testid="product-custom-amount" className="w-28 px-2 py-1 rounded border text-2xl" style={{ borderColor: 'rgba(0,0,0,0.2)', color: 'var(--ink)' }} />
            </label>
          ) : (
            <div className="mb-3">
              <span className="font-title text-3xl" style={{ color: 'var(--maroon)' }} data-testid="product-price">{money(p.your_price)}</span>
              {p.member_eligible && p.price_member < p.price_public && <span className="text-sm line-through ml-2" style={{ color: 'var(--olive)' }}>{money(p.price_public)}</span>}
              {!p.member_eligible && p.price_member < p.price_public && <div className="text-xs" style={{ color: 'var(--olive)' }}>Financial members pay {money(p.price_member)}</div>}
            </div>
          )}
          <p className="text-[15px] whitespace-pre-line mb-4" data-testid="product-description">{p.description || 'No description yet.'}</p>
          {p.track_stock !== false && <p className="text-sm mb-1" style={{ color: inStock ? 'var(--olive)' : '#8a1f11' }} data-testid="product-stock">{inStock ? `${p.stock} in stock` : 'Sold out'}</p>}
          {p.postage > 0 && <p className="text-sm mb-4 inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}><Truck size={14} /> Postage {money(p.postage)} each if delivered; free pickup</p>}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {inStock && <button onClick={addToCart} data-testid="product-add-to-cart" className="inline-flex items-center gap-2 px-5 py-3 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><ShoppingBag size={16} /> {p.category === 'Donation' ? 'Donate' : p.category === 'Membership' ? 'Renew' : 'Add to cart'}</button>}
            <button onClick={toggleLike} data-testid="product-like" className="inline-flex items-center gap-1 px-4 py-3 rounded border font-title" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)' }} title={user ? 'Like' : 'Sign in to like'}>
              <Heart size={16} fill={p.liked ? 'var(--maroon)' : 'none'} /> {p.liked ? 'Liked' : 'Like'} <span className="text-sm" data-testid="product-like-count">({p.like_count || 0})</span>
            </button>
            <button onClick={toggleFav} data-testid="product-favourite" className="inline-flex items-center gap-1 px-4 py-3 rounded border font-title" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }} title={user ? 'Favourite' : 'Sign in to save favourites'}>
              <Bookmark size={16} fill={p.favourited ? 'var(--navy)' : 'none'} /> {p.favourited ? 'Favourited' : 'Favourite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
